/* assets/country-welcome.js
   Drives the first-visit country popup (sections/country-welcome.liquid).

   Asks Shopify whether the visitor appears to be somewhere other than the country they're
   browsing in (IP + browser settings), then shows one of two states:
     - mismatch: "Shopping from X?" + Switch (submits Shopify's localization form)
     - match:    "You're shopping in X." + per-country bullets + Continue shopping
   Never redirects on its own. See
   https://shopify.dev/docs/storefronts/themes/markets/localization-discovery

   Avoiding clashes with the manual country selector (snippets/country-selector-modal.liquid):
     - Shown once per location. Any choice in this popup, or submitting the selector's form,
       marks it done and stores the country the visitor was detected in at the time.
     - Returning visitors are re-checked once per browser session. They're only asked again
       (mismatch state only) if they've been detected somewhere new AND Shopify suggests a
       country other than the one they're browsing in, e.g. a UK shopper now in France still
       browsing in GBP. A manual choice is never second-guessed while they stay put.
     - Never opens while the selector is open (body.country-modal-open).
     - "Choose your shipping destination" closes this popup; the selector's own delegated
       [data-open-country-modal] listener then opens the selector.
   Dispatches `country-welcome:closed` on document when dismissed, so other popups
   (e.g. a Klaviyo form set to a custom trigger) can wait for it. */
(function () {
  'use strict';

  // Value is the country code the visitor was detected in when they decided ('?' if unknown).
  var STORAGE_KEY = 'cg-country-welcome-done';
  var SESSION_KEY = 'cg-country-welcome-checked';
  var OPEN_CLASS = 'country-welcome-open';

  var detectedCountry = null;
  var decidedThisPage = false;

  function getDone() {
    try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
  }

  function setDone() {
    decidedThisPage = true;
    try { localStorage.setItem(STORAGE_KEY, detectedCountry || '?'); } catch (e) {}
  }

  function checkedThisSession() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { return false; }
  }

  function setCheckedThisSession() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
  }

  function init(modal) {
    var designMode = !!(window.Shopify && window.Shopify.designMode);
    var currentCountry = modal.getAttribute('data-current-country');
    var lastFocused = null;

    function setState(state) {
      modal.querySelectorAll('[data-country-welcome-state]').forEach(function (el) {
        el.hidden = el.getAttribute('data-country-welcome-state') !== state;
      });
    }

    // Show the block listing this country, else the block with no countries (the default).
    function showMessageFor(code) {
      var messages = modal.querySelectorAll('[data-country-welcome-message]');
      var match = null;
      var fallback = null;
      messages.forEach(function (el) {
        var codes = (el.getAttribute('data-countries') || '').split(',').filter(Boolean);
        if (!codes.length && !fallback) fallback = el;
        if (codes.indexOf(code) !== -1 && !match) match = el;
      });
      var chosen = match || fallback;
      messages.forEach(function (el) { el.hidden = el !== chosen; });
    }

    function showMessageBlock(blockEl) {
      modal.querySelectorAll('[data-country-welcome-message]').forEach(function (el) {
        el.hidden = el !== blockEl;
      });
    }

    function open() {
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.body.classList.add(OPEN_CLASS);
      document.body.classList.remove('reveal-mobile-nav');
      document.addEventListener('keydown', onKeydown);
      window.setTimeout(function () {
        var primary = modal.querySelector('[data-country-welcome-state]:not([hidden]) .country-modal__update');
        (primary || modal.querySelector('.country-modal__close')).focus();
      }, 0);
    }

    function close(restoreFocus) {
      if (modal.hidden) return;
      modal.hidden = true;
      document.body.classList.remove(OPEN_CLASS);
      document.removeEventListener('keydown', onKeydown);
      if (restoreFocus !== false && lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      document.dispatchEvent(new CustomEvent('country-welcome:closed'));
    }

    function dismiss(restoreFocus) {
      if (!designMode) setDone();
      close(restoreFocus);
    }

    function onKeydown(evt) {
      if (evt.key === 'Escape' || evt.keyCode === 27) {
        dismiss();
        return;
      }
      if (evt.key === 'Tab' || evt.keyCode === 9) trapFocus(evt);
    }

    // aria-modal="true" promises focus stays in the dialog, so wrap Tab at both ends.
    function trapFocus(evt) {
      var focusable = Array.prototype.filter.call(
        modal.querySelectorAll('button, input:not([type="hidden"]), [href], [tabindex]:not([tabindex="-1"])'),
        function (el) { return !el.disabled && el.offsetParent !== null; }
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (evt.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) {
        evt.preventDefault();
        first.focus();
      }
    }

    modal.querySelectorAll('[data-country-welcome-close], [data-country-welcome-continue]').forEach(function (el) {
      el.addEventListener('click', function () { dismiss(); });
    });

    // Hand over to the selector: close without restoring focus (the selector takes it).
    // Runs before the selector's document-level [data-open-country-modal] listener.
    modal.querySelector('[data-country-welcome-choose]').addEventListener('click', function () {
      dismiss(false);
    });

    var form = modal.querySelector('#CountryWelcomeForm');
    if (form) form.addEventListener('submit', function () { if (!designMode) setDone(); });

    // Theme editor: preview the popup while its section or one of its blocks is selected.
    if (designMode) {
      var sectionId = modal.getAttribute('data-section-id');
      // Listeners outlive a re-rendered section; ignore events once this node is gone.
      var isLive = function () { return document.body.contains(modal); };
      document.addEventListener('shopify:section:select', function (evt) {
        if (!isLive() || evt.detail.sectionId !== sectionId) return;
        setState('match');
        showMessageFor(currentCountry);
        open();
      });
      document.addEventListener('shopify:section:deselect', function (evt) {
        if (isLive() && evt.detail.sectionId === sectionId) close(false);
      });
      document.addEventListener('shopify:block:select', function (evt) {
        if (!isLive() || evt.detail.sectionId !== sectionId) return;
        setState('match');
        showMessageBlock(evt.target);
        open();
      });
      return;
    }

    // Detected country at the time of their last decision, or null for a first visit.
    var previousDetected = getDone();
    if (previousDetected !== null) {
      if (checkedThisSession()) return;
      setCheckedThisSession();
    }

    var showInHomeCountry = modal.getAttribute('data-show-in-home-country') === 'true';
    var homeCountry = modal.getAttribute('data-home-country');
    var delay = (parseInt(modal.getAttribute('data-delay'), 10) || 0) * 1000;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'browsing_context_suggestions.json' +
      '?country[enabled]=true' +
      '&country[exclude]=' + encodeURIComponent(currentCountry);

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        var suggestion = data.suggestions && data.suggestions[0];
        var suggested = suggestion && suggestion.parts && suggestion.parts.country;
        var detected = data.detected_values && data.detected_values.country;
        detectedCountry = (detected && detected.handle) || null;

        // Returning visitor: only re-ask if they've moved AND are browsing in the wrong country.
        if (previousDetected !== null) {
          if (!suggested || !suggested.handle || !detectedCountry || detectedCountry === previousDetected) return;
        }

        if (suggested && suggested.handle) {
          setState('mismatch');
          modal.querySelector('[data-country-welcome-input]').value = suggested.handle;
          modal.querySelectorAll('[data-country-welcome-suggested-name]').forEach(function (el) {
            el.textContent = suggested.name;
          });
          showMessageFor(suggested.handle);
        } else {
          if (currentCountry === homeCountry && !showInHomeCountry) return;
          setState('match');
          showMessageFor(currentCountry);
        }

        window.setTimeout(function () {
          // The visitor is already choosing manually; don't stack a second popup on top.
          if (decidedThisPage || document.body.classList.contains('country-modal-open')) return;
          open();
        }, delay);
      })
      .catch(function () {});
  }

  // Submitting the manual selector is a decision too; never follow it with this popup.
  document.addEventListener('submit', function (evt) {
    if (evt.target && evt.target.id === 'CountryModalForm') setDone();
  });

  function boot() {
    var modal = document.querySelector('[data-country-welcome]');
    if (modal) init(modal);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  // Theme editor re-renders the section on setting changes.
  document.addEventListener('shopify:section:load', function (evt) {
    var modal = evt.target.querySelector('[data-country-welcome]');
    if (!modal) return;
    document.body.classList.remove(OPEN_CLASS);
    init(modal);
  });
})();
